import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { HasPermissionDirective } from '../../../utils/has-permission.directive';
import { ConfirmAlertComponent } from '../../shared/confirm-alert/confirm-alert.component';
import { FullSpinnerService } from '../../../services/full-spinner.service';
import { AppConstants } from '../../../utils/AppConstants';
import { AlertService } from '../../shared/alert/alert.service';
import { Guardian } from '../../../models/Guardian';
import { GuardianAddress } from '../../../models/GuardianAddress';
import { GuardianService } from '../../../services/guardian.service';
import { CityAutocompleteComponent } from '../../shared/city-autocomplete/city-autocomplete.component';

@Component({
  selector: 'app-guardian-detail',
  standalone: true,
  imports: [
    CommonModule,
    HasPermissionDirective,
    ConfirmAlertComponent,
    FormsModule,
    ReactiveFormsModule,
    CityAutocompleteComponent,
  ],
  templateUrl: './guardian-detail.component.html',
  styleUrl: './guardian-detail.component.scss',
})
export class GuardianDetailComponent implements OnInit, OnDestroy {
  paramSubscription!: Subscription;
  showConfirmationModal: boolean = false;
  confirmationMessage: string = '';

  id?: number;
  guardian: Guardian = { authorizedByPrefect: false, addresses: [] };
  detail_title: string = 'Nuovo deposito giudiziario';

  constructor(
    private guardianService: GuardianService,
    private alertService: AlertService,
    private fullSpinnerService: FullSpinnerService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.paramSubscription = this.activatedRoute.params.subscribe((parameter) => {
      this.id = parameter['id'];
      if (this.id! > 0) {
        this.fullSpinnerService.spinner('');
        this.guardianService.get(this.id!).subscribe({
          next: (res) => {
            this.guardian = res;
            if (!this.guardian.addresses) this.guardian.addresses = [];
            this.detail_title = res.companyName!;
          },
          complete: () => {
            this.fullSpinnerService.spinner(undefined);
          },
          error: () => {
            this.fullSpinnerService.spinner(undefined);
          },
        });
      } else {
        // Nuovo deposito: parte con una sede da compilare (almeno una obbligatoria).
        this.addAddress();
      }
    });
  }

  addAddress(): void {
    this.guardian.addresses = this.guardian.addresses || [];
    this.guardian.addresses.push({
      name: '',
      address: '',
      city: '',
      province: '',
      postalCode: '',
      phone: '',
      email: '',
    } as GuardianAddress);
  }

  removeAddress(index: number): void {
    if (this.guardian.addresses && this.guardian.addresses.length > 1) {
      this.guardian.addresses.splice(index, 1);
    }
  }

  onSubmit(form: NgForm): void {
    if (!this.guardian.addresses || this.guardian.addresses.length === 0) {
      this.alertService.addErrors(
        AppConstants.titleErrorMessage,
        ['È obbligatoria almeno una sede.'],
        true
      );
      return;
    }
    if (form.valid) {
      this.fullSpinnerService.spinner('');
      this.guardianService.save(this.guardian).subscribe({
        next: () => {
          this.alertService.addErrors(
            AppConstants.titleSuccessMessage,
            [AppConstants.successMessage],
            true
          );
          this.back();
        },
        complete: () => {
          this.fullSpinnerService.spinner(undefined);
        },
        error: () => {
          this.alertService.addErrors(
            AppConstants.titleErrorMessage,
            [AppConstants.errorMessage],
            true
          );
          this.fullSpinnerService.spinner(undefined);
        },
      });
    }
  }

  back(): void {
    this.router.navigate(['organization-config'], {
      queryParams: { tab: 'judicial-deposits' },
    });
  }

  confirmDelete(): void {
    this.showConfirmationModal = true;
    this.confirmationMessage =
      'Sicuro di voler eliminare ' + this.guardian.companyName + ' ?';
  }

  cancelDelete(): void {
    this.showConfirmationModal = false;
  }

  delete(): void {
    this.showConfirmationModal = false;
    this.fullSpinnerService.spinner('');
    this.guardianService.delete(this.guardian.id!).subscribe({
      next: () => {
        this.alertService.addErrors(
          AppConstants.titleDeleteSuccessMessage,
          [AppConstants.successMessage],
          true
        );
        this.back();
      },
      complete: () => {
        this.fullSpinnerService.spinner(undefined);
      },
      error: () => {
        this.alertService.addErrors(
          AppConstants.titleErrorMessage,
          [AppConstants.errorMessage],
          true
        );
        this.fullSpinnerService.spinner(undefined);
      },
    });
  }

  ngOnDestroy(): void {
    this.paramSubscription.unsubscribe();
  }
}
